import { useEffect } from 'react';
import { GAME_PASSPORT_EVENT, mutateGamePassport, readGamePassport, reconcileGamePassport, type GamePassport, type GameTicket } from './game-passport';
import { captureLocalCommunitySnapshot, isLocalCommunityGenerationCurrent, transmitIfLocalCommunityGeneration } from './outbox';
import { mayPoll, retryAfterMs } from './public-reads';
import type { Fixture, FixtureSource } from './types';

export const GAME_ARCHIVE_READS_KEY = 'coyg_game_archive_reads_v1';
export const GAME_ARCHIVE_DAILY_LIMIT = 4;
const ARCHIVE_LOCK = 'coyg-game-archive-reads';
const MAX_RECORD_BYTES = 64_000;
const FOUR_HOURS = 4 * 60 * 60_000;

type Budget = { day: string; calls: number; nextAt: number; attempted: Record<string, number> };
type RecordData = { matchId: string; productScope: string; kickoffUtc: string; comp: string; opponent: string; opponentShort: string; venue: Fixture['venue']; arsenalSide?: 'home' | 'away'; score: { home: number; away: number }; checkedAt: string; sourceUrl: string; source: string; truthProviderId?: string };
let flight: Promise<void> | null = null;
let context: { ids: Set<string>; enabled: boolean } = { ids: new Set(), enabled: false };

const text = (value: unknown, max: number): value is string => typeof value === 'string' && value.trim().length > 0 && value.length <= max;
const object = (value: unknown): value is Record<string, unknown> => Boolean(value && typeof value === 'object' && !Array.isArray(value));
function strictUtc(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(value)) return false;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) && date.toISOString() === (value.includes('.') ? value : value.replace('Z', '.000Z'));
}
function safeUrl(value: unknown): value is string {
  if (!text(value, 1000)) return false;
  try { const url = new URL(value); return url.protocol === 'https:' && !url.username && !url.password; } catch { return false; }
}
async function boundedRecordText(response: Response): Promise<string | null> {
  if (!response.body) return null;
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let size = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      size += value.byteLength;
      if (size > MAX_RECORD_BYTES) { await reader.cancel(); return null; }
      chunks.push(value);
    }
  } finally { reader.releaseLock(); }
  const body = new Uint8Array(size); let offset = 0;
  for (const chunk of chunks) { body.set(chunk, offset); offset += chunk.byteLength; }
  return new TextDecoder().decode(body);
}
function validEvent(value: unknown): boolean {
  return object(value) && ['goal', 'own-goal', 'pen', 'red', 'yellow', 'sub', 'var', 'note'].includes(String(value.type))
    && (value.min === null || (typeof value.min === 'number' && Number.isInteger(value.min) && value.min >= 0 && value.min <= 180))
    && (value.ours == null || typeof value.ours === 'boolean') && (value.player == null || text(value.player, 120))
    && (value.detail == null || text(value.detail, 500));
}
function pending(ticket: GameTicket, now: number): boolean {
  const kickoff = Date.parse(ticket.fixture.kickoffUtc ?? `${ticket.fixture.kickoffDate ?? ''}T12:00:00Z`);
  return !context.ids.has(ticket.fixture.id) && ticket.outcome === null && Boolean(ticket.call || ticket.joined || ticket.saved)
    && /^[a-z0-9-]{1,100}$/.test(ticket.fixture.id) && Number.isFinite(kickoff) && kickoff <= now - FOUR_HOURS;
}
function available(): boolean {
  return context.enabled && mayPoll() && typeof navigator !== 'undefined' && Boolean(navigator.locks);
}
async function withBudgetLock<T>(task: () => T | Promise<T>): Promise<T | null> {
  if (typeof navigator === 'undefined' || !navigator.locks) return null;
  try { return await navigator.locks.request(ARCHIVE_LOCK, { mode: 'exclusive' }, task); } catch { return null; }
}
function readBudget(now: number): Budget | null {
  try {
    const raw = localStorage.getItem(GAME_ARCHIVE_READS_KEY);
    const day = new Date(now).toISOString().slice(0, 10);
    if (!raw) return { day, calls: 0, nextAt: 0, attempted: {} };
    if (raw.length > 20_000) return null;
    const parsed = JSON.parse(raw) as Budget;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(parsed.day) || !Number.isInteger(parsed.calls) || parsed.calls < 0 || parsed.calls > GAME_ARCHIVE_DAILY_LIMIT
      || !Number.isFinite(parsed.nextAt) || parsed.nextAt < 0 || !object(parsed.attempted)) return null;
    const attempted = Object.fromEntries(Object.entries(parsed.attempted).filter(([id, at]) => /^[a-z0-9-]{1,100}$/.test(id) && typeof at === 'number' && Number.isFinite(at) && at >= 0).slice(-200));
    return { day, calls: parsed.day === day ? parsed.calls : 0, nextAt: parsed.nextAt, attempted };
  } catch { return null; }
}
function writeBudget(value: Budget): boolean {
  try {
    const encoded = JSON.stringify(value);
    localStorage.setItem(GAME_ARCHIVE_READS_KEY, encoded);
    return localStorage.getItem(GAME_ARCHIVE_READS_KEY) === encoded;
  } catch { return false; }
}
async function selectAndReserve(state: GamePassport, generation: string): Promise<GameTicket | null> {
  const reservation = await transmitIfLocalCommunityGeneration(generation, () => withBudgetLock(() => {
    const now = Date.now();
    if (!available()) return null;
    const budget = readBudget(now);
    if (!budget || budget.calls >= GAME_ARCHIVE_DAILY_LIMIT || budget.nextAt > now) return null;
    const start = Date.parse(`${budget.day}T00:00:00Z`);
    const candidates = state.tickets.filter((ticket) => pending(ticket, now) && (budget.attempted[ticket.fixture.id] ?? -1) < start)
      .sort((a, b) => (budget.attempted[a.fixture.id] ?? -1) - (budget.attempted[b.fixture.id] ?? -1)
        || Date.parse(a.fixture.kickoffUtc ?? a.fixture.kickoffDate ?? '') - Date.parse(b.fixture.kickoffUtc ?? b.fixture.kickoffDate ?? '')
        || a.fixture.id.localeCompare(b.fixture.id));
    const ticket = candidates[0];
    if (!ticket) return null;
    const attempted = { ...budget.attempted, [ticket.fixture.id]: now };
    const bounded = Object.fromEntries(Object.entries(attempted).sort((a, b) => b[1] - a[1]).slice(0, 200));
    return writeBudget({ ...budget, calls: budget.calls + 1, attempted: bounded }) ? ticket : null;
  }), 'shared');
  return reservation.transmitted ? reservation.value : null;
}
async function postponeReads(delay: number, generation: string): Promise<void> {
  if (!(delay > 0)) return;
  await transmitIfLocalCommunityGeneration(generation, () => withBudgetLock(() => {
    const budget = readBudget(Date.now());
    if (budget) writeBudget({ ...budget, nextAt: Math.max(budget.nextAt, Date.now() + delay) });
  }), 'shared');
}

/** Permanent records are checked against the retained identity, never just an ID. */
function validRecord(value: unknown, ticket: GameTicket, now: number): value is RecordData {
  if (!object(value) || !object(value.score)) return false;
  const fixture = ticket.fixture;
  const sourceUrls = value.sourceUrls ?? [value.sourceUrl];
  return value.matchId === fixture.id && value.productScope === 'arsenal-men-first-team'
    && value.comp === fixture.comp && value.opponent === fixture.opponent && value.venue === fixture.venue
    && (fixture.venue === 'N' ? ['home', 'away'].includes(String(value.arsenalSide)) && value.arsenalSide === fixture.arsenalSide : value.arsenalSide == null)
    && text(value.opponentShort, 20) && strictUtc(value.kickoffUtc) && Date.parse(value.kickoffUtc) < now
    && [value.score.home, value.score.away].every((score) => typeof score === 'number' && Number.isInteger(score) && score >= 0 && score <= 99)
    && ['score', 'goals', 'full'].includes(String(value.verification)) && Array.isArray(value.events) && value.events.length <= 100 && value.events.every(validEvent)
    && text(value.summary, 2000) && text(value.source, 120) && safeUrl(value.sourceUrl)
    && Array.isArray(sourceUrls) && sourceUrls.length >= 1 && sourceUrls.length <= 10 && sourceUrls.includes(value.sourceUrl) && sourceUrls.every(safeUrl)
    && strictUtc(value.checkedAt) && Date.parse(value.checkedAt) >= Date.parse(value.kickoffUtc) && Date.parse(value.checkedAt) <= now + 5 * 60_000
    && strictUtc(value.updatedAt) && Date.parse(value.updatedAt) <= now + 5 * 60_000
    && Number.isInteger(value.revision) && Number(value.revision) >= 1;
}
async function processTicket(ticket: GameTicket, generation: string): Promise<void> {
  if (!available() || !(await isLocalCommunityGenerationCurrent(generation))) return;
  try {
    const response = await fetch(`/api/matches/${encodeURIComponent(ticket.fixture.id)}/record`, {
      cache: 'no-store', credentials: 'same-origin', redirect: 'error', signal: AbortSignal.timeout(8_000),
    });
    await postponeReads(Math.max(retryAfterMs(response.headers.get('retry-after')), response.status === 429 ? 60_000 : 0), generation);
    if (!response.ok) { await response.body?.cancel(); return; }
    const encoded = await boundedRecordText(response);
    if (!encoded) return;
    const record: unknown = JSON.parse(encoded);
    if (!validRecord(record, ticket, Date.now()) || !available() || !(await isLocalCommunityGenerationCurrent(generation))) return;
    await mutateGamePassport((state) => {
      const retained = state.tickets.find((item) => item.fixture.id === ticket.fixture.id);
      if (!retained || !pending(retained, Date.now()) || !validRecord(record, retained, Date.now())) return state;
      const fixture: Fixture = { ...retained.fixture, kickoffUtc: record.kickoffUtc, kickoffDate: undefined, timeTbc: false,
        statusQuality: undefined, status: 'ft', opponentShort: record.opponentShort, score: record.score };
      const openLiga = new URL(record.sourceUrl).origin === 'https://api.openligadb.de';
      const provider = openLiga ? 'openligadb' : text(record.truthProviderId, 160) ? record.truthProviderId : record.source;
      const sameProvider = provider === retained.sourceProvider;
      const licenseUrl = openLiga ? 'https://www.openligadb.de/lizenz' : sameProvider ? retained.licenseUrl : null;
      const licenseLabel = openLiga ? 'ODbL 1.0' : sameProvider ? retained.licenseLabel : null;
      const source: FixtureSource = { provider, url: record.sourceUrl,
        checkedAt: record.checkedAt, ...(licenseUrl ? { licenseUrl } : {}),
        ...(licenseLabel ? { licenseLabel } : {}), scope: 'selected', fixtureCount: 1, rights: 'approved', status: 'fresh' };
      return reconcileGamePassport(state, [fixture], retained.season, source, false);
    }, generation);
  } catch { /* Unavailable or invalid evidence leaves the retained ticket pending. */ }
}

/** Event-driven catch-up only: four persisted, same-origin archive reads per UTC day. */
export function runArchivedGameCatchup(fixtures: Fixture[], enabled: boolean): Promise<void> {
  context = { ids: new Set(fixtures.map((fixture) => fixture.id)), enabled };
  if (!available()) return Promise.resolve();
  if (flight) return flight;
  flight = (async () => {
    for (let count = 0; count < GAME_ARCHIVE_DAILY_LIMIT && available(); count += 1) {
      const snapshot = await captureLocalCommunitySnapshot(readGamePassport);
      if (!snapshot.value) return;
      const ticket = await selectAndReserve(snapshot.value, snapshot.generation);
      if (!ticket) return;
      await processTicket(ticket, snapshot.generation);
    }
  })().catch(() => { /* Storage and locks fail closed. */ }).finally(() => { flight = null; });
  return flight;
}

export function useArchivedGameCatchup(fixtures: Fixture[], enabled: boolean): void {
  const signature = JSON.stringify(fixtures.map((fixture) => [fixture.id, fixture.kickoffUtc, fixture.status, fixture.score]));
  useEffect(() => {
    const update = () => { void runArchivedGameCatchup(fixtures, enabled); };
    update();
    window.addEventListener(GAME_PASSPORT_EVENT, update);
    window.addEventListener('online', update);
    document.addEventListener('visibilitychange', update);
    return () => {
      window.removeEventListener(GAME_PASSPORT_EVENT, update);
      window.removeEventListener('online', update);
      document.removeEventListener('visibilitychange', update);
    };
  }, [signature, enabled]);
}
